<template>
  <div class="page">
    <header class="page-header">
      <div>
        <h1>Usuários</h1>
        <p class="muted" v-if="encontro">
          Admins e voluntários do encontro <strong>{{ encontro.nome }}</strong>
          (<code>{{ encontro.slug }}.checkin...</code>)
        </p>
      </div>
      <button class="btn-secondary" @click="$router.push('/')">← Encontros</button>
    </header>

    <section class="card">
      <header class="section-header">
        <h2>Cadastrados</h2>
        <button class="btn-primary" @click="abrirCriar">+ Novo usuário</button>
      </header>

      <div v-if="loading" class="empty">Carregando…</div>
      <div v-else-if="!admins.length" class="empty">
        <p class="muted">Nenhum admin/voluntário cadastrado neste encontro.</p>
      </div>
      <table v-else class="tabela">
        <thead>
          <tr>
            <th>Nome</th>
            <th>Email</th>
            <th>Cargo</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="a in admins" :key="a.id">
            <td>{{ a.name }}</td>
            <td>{{ a.email }}</td>
            <td>
              <span class="role-badge" :class="`role-${a.role.toLowerCase()}`">{{ roleLabel(a.role) }}</span>
            </td>
            <td class="acoes">
              <button class="btn-link" @click="abrirSenha(a)">Trocar senha</button>
              <button v-if="a.role !== 'ROOT'" class="btn-link danger" @click="remover(a)" :disabled="removendoId === a.id">
                {{ removendoId === a.id ? 'Removendo…' : 'Remover' }}
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </section>

    <!-- Modal criar -->
    <transition name="fade">
      <div v-if="modalCriar" class="modal-overlay" @click.self="modalCriar = false">
        <div class="modal-card">
          <h3>Novo usuário</h3>
          <form @submit.prevent="criar">
            <div class="form-group">
              <label class="label">Nome</label>
              <input class="input" v-model="formNovo.name" required maxlength="120" />
            </div>
            <div class="form-group">
              <label class="label">Email</label>
              <input class="input" type="email" v-model="formNovo.email" required maxlength="120" />
            </div>
            <div class="form-group">
              <label class="label">Senha (mín 6)</label>
              <input class="input" type="text" v-model="formNovo.password" required minlength="6" />
            </div>
            <div class="form-group">
              <label class="label">Cargo</label>
              <select class="input" v-model="formNovo.role">
                <option value="CHECKIN">Voluntário (CHECKIN)</option>
                <option value="ADMIN">Admin (ADMIN)</option>
                <option value="ROOT">Root do encontro (ROOT)</option>
              </select>
            </div>
            <p v-if="erroNovo" class="erro">{{ erroNovo }}</p>
            <div class="modal-actions">
              <button type="button" class="btn-secondary" @click="modalCriar = false">Cancelar</button>
              <button type="submit" class="btn-primary" :disabled="criando">{{ criando ? 'Criando…' : 'Criar' }}</button>
            </div>
          </form>
        </div>
      </div>
    </transition>

    <!-- Modal senha -->
    <transition name="fade">
      <div v-if="senhaTarget" class="modal-overlay" @click.self="senhaTarget = null">
        <div class="modal-card">
          <h3>Trocar senha</h3>
          <p class="muted">{{ senhaTarget.name }} ({{ senhaTarget.email }})</p>
          <form @submit.prevent="trocarSenha">
            <div class="form-group">
              <label class="label">Nova senha (mín 6)</label>
              <input class="input" type="text" v-model="novaSenha" required minlength="6" />
            </div>
            <p v-if="erroSenha" class="erro">{{ erroSenha }}</p>
            <div class="modal-actions">
              <button type="button" class="btn-secondary" @click="senhaTarget = null">Cancelar</button>
              <button type="submit" class="btn-primary" :disabled="trocandoSenha">{{ trocandoSenha ? 'Salvando…' : 'Salvar' }}</button>
            </div>
          </form>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import api from '../lib/api'

export default {
  name: 'EncontroAdminsPage',
  data() {
    return {
      admins: [],
      encontro: null,
      loading: true,
      removendoId: null,
      modalCriar: false,
      formNovo: { name: '', email: '', password: '', role: 'CHECKIN' },
      criando: false,
      erroNovo: '',
      senhaTarget: null,
      novaSenha: '',
      trocandoSenha: false,
      erroSenha: ''
    }
  },
  async mounted() {
    await this.carregar()
  },
  methods: {
    roleLabel(role) {
      if (role === 'ROOT') return 'Root'
      if (role === 'ADMIN') return 'Admin'
      if (role === 'CHECKIN') return 'Voluntário'
      return role
    },
    async carregar() {
      this.loading = true
      try {
        const { data } = await api.get(`/encontros/${this.$route.params.id}/admins`)
        this.admins = data.admins || []
        this.encontro = data.encontro || null
      } catch (err) {
        this.$toast.open({ message: err.response?.data?.message || 'Falha ao listar usuários', type: 'error', position: 'top-right' })
      } finally {
        this.loading = false
      }
    },
    abrirCriar() {
      this.formNovo = { name: '', email: '', password: '', role: 'CHECKIN' }
      this.erroNovo = ''
      this.modalCriar = true
    },
    async criar() {
      this.criando = true
      this.erroNovo = ''
      try {
        await api.post(`/encontros/${this.$route.params.id}/admins`, this.formNovo)
        this.$toast.open({ message: 'Usuário criado', type: 'success', position: 'top-right' })
        this.modalCriar = false
        await this.carregar()
      } catch (err) {
        const detail = err.response?.data
        this.erroNovo = Array.isArray(detail?.errors)
          ? detail.errors.map(e => `${e.field}: ${e.message}`).join(' · ')
          : detail?.message || 'Falha ao criar usuário'
      } finally {
        this.criando = false
      }
    },
    async remover(a) {
      if (!confirm(`Remover ${a.name} (${a.email})?`)) return
      this.removendoId = a.id
      try {
        await api.delete(`/encontros/${this.$route.params.id}/admins/${a.id}`)
        this.$toast.open({ message: 'Usuário removido', type: 'success', position: 'top-right' })
        await this.carregar()
      } catch (err) {
        this.$toast.open({ message: err.response?.data?.message || 'Falha ao remover', type: 'error', position: 'top-right' })
      } finally {
        this.removendoId = null
      }
    },
    abrirSenha(a) {
      this.senhaTarget = a
      this.novaSenha = ''
      this.erroSenha = ''
    },
    async trocarSenha() {
      this.trocandoSenha = true
      this.erroSenha = ''
      try {
        await api.patch(`/encontros/${this.$route.params.id}/admins/${this.senhaTarget.id}/password`, { password: this.novaSenha })
        this.$toast.open({ message: 'Senha atualizada', type: 'success', position: 'top-right' })
        this.senhaTarget = null
      } catch (err) {
        this.erroSenha = err.response?.data?.message || 'Falha ao trocar senha'
      } finally {
        this.trocandoSenha = false
      }
    }
  }
}
</script>

<style scoped>
.page { max-width: 880px; margin: 0 auto; padding: var(--spacing-base); display: flex; flex-direction: column; gap: var(--spacing-base); }
.page-header { display: flex; justify-content: space-between; align-items: flex-start; gap: var(--spacing-base); flex-wrap: wrap; }
.page-header h1 { margin: 0 0 4px; }
.muted { color: var(--color-text-muted); margin: 0; font-size: var(--text-sm); }
code { background: #f1f5f9; padding: 1px 4px; border-radius: 3px; font-size: 0.9em; }
.card { background: white; border-radius: var(--radius-md); border: 1px solid var(--color-border); padding: var(--spacing-base); }
.section-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: var(--spacing-base); }
.section-header h2 { margin: 0; }
.empty { padding: var(--spacing-base) 0; text-align: center; }
.tabela { width: 100%; border-collapse: collapse; }
.tabela th, .tabela td { padding: 10px 8px; text-align: left; border-bottom: 1px solid #e5e7eb; }
.tabela th { font-size: 12px; color: var(--color-text-muted); text-transform: uppercase; font-weight: 600; }
.acoes { display: flex; gap: 8px; justify-content: flex-end; }
.role-badge { padding: 2px 8px; border-radius: 99px; font-size: 11px; font-weight: 600; text-transform: uppercase; }
.role-root { background: #fef3c7; color: #92400e; }
.role-admin { background: #dbeafe; color: #1e40af; }
.role-checkin { background: #f3e8ff; color: #6b21a8; }
.btn-primary, .btn-secondary { padding: 8px 14px; border-radius: var(--radius-md); font-weight: 500; cursor: pointer; border: 0; }
.btn-primary { background: #2563eb; color: white; }
.btn-secondary { background: #e2e8f0; color: #0f172a; }
.btn-link { background: none; border: none; color: #2563eb; cursor: pointer; padding: 0; font-size: var(--text-sm); text-decoration: underline; }
.btn-link.danger { color: #dc2626; }
.btn-link:disabled { opacity: 0.5; cursor: not-allowed; }
.modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 100; padding: var(--spacing-base); }
.modal-card { background: white; border-radius: var(--radius-md); padding: var(--spacing-lg); max-width: 460px; width: 100%; box-shadow: 0 10px 40px rgba(0,0,0,0.2); }
.modal-card h3 { margin: 0 0 8px; }
.form-group { display: flex; flex-direction: column; gap: 4px; margin-bottom: var(--spacing-sm); }
.label { font-size: 12px; font-weight: 600; color: #374151; text-transform: uppercase; }
.input { padding: 10px 12px; border: 1px solid #d1d5db; border-radius: var(--radius-md); font-size: var(--text-base); }
.modal-actions { display: flex; gap: 8px; justify-content: flex-end; margin-top: var(--spacing-base); }
.erro { background: #fee2e2; color: #b91c1c; padding: 8px 12px; border-radius: var(--radius-md); font-size: var(--text-sm); margin: 8px 0; }
.fade-enter-active, .fade-leave-active { transition: opacity 0.15s; }
.fade-enter-from, .fade-leave-to { opacity: 0; }
</style>
